/**
 * Editor for a chart's transversal `filters` (FilterSpec[]). Rendered by the
 * WidgetEditor only for chart types whose `capabilities.filterable` is true.
 *
 * Each filter is one row: a column picker, a kind toggle (categorical | numeric)
 * and the kind's value inputs — a checkbox list of the column's distinct values
 * for categorical, min/max numbers for numeric. Emits the whole array up via
 * `onChange`. Categorical values come from `getValues(field)` rather than free
 * text, so a value containing a comma can still be selected exactly.
 */
import { Plus, Trash2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  FieldSelect,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@micronet/ui-kit';
import type { FilterSpec } from '../charts/model';

interface FiltersEditorProps {
  filters: FilterSpec[];
  fields: string[];
  /** Distinct values of a column, for the categorical value picker. */
  getValues: (field: string) => string[];
  onChange: (filters: FilterSpec[]) => void;
}

/** Checkbox list of a column's distinct values for a categorical filter. */
function CategoricalValuePicker({
  index,
  selected,
  available,
  onChange,
}: {
  index: number;
  selected: string[];
  available: string[];
  onChange: (values: string[]) => void;
}) {
  if (available.length === 0) {
    return (
      <p className="flex-1 text-sm text-muted-foreground">Sin valores en esta columna.</p>
    );
  }
  const toggle = (value: string, on: boolean) =>
    onChange(on ? [...selected, value] : selected.filter((v) => v !== value));

  return (
    <div className="flex max-h-24 flex-1 flex-wrap items-center gap-x-3 gap-y-1 overflow-y-auto">
      {available.map((value, vi) => {
        const id = `filter-${index}-val-${vi}`;
        return (
          <span key={id} className="flex items-center gap-1">
            <Checkbox
              id={id}
              checked={selected.includes(value)}
              onCheckedChange={(c) => toggle(value, c === true)}
            />
            <Label htmlFor={id} className="text-sm font-normal">
              {value}
            </Label>
          </span>
        );
      })}
    </div>
  );
}

/** Parse a numeric input, mapping blank/non-numeric to undefined (no bound). */
function parseBound(raw: string): number | undefined {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function FiltersEditor({ filters, fields, getValues, onChange }: FiltersEditorProps) {
  const update = (i: number, next: FilterSpec) =>
    onChange(filters.map((f, idx) => (idx === i ? next : f)));

  // Guarded by the disabled "Agregar filtro" button below, but keep the no-field
  // case a no-op too: a filter with an empty `field` is invalid (the column
  // Select can't render an empty value).
  const add = () => {
    if (fields.length === 0) return;
    onChange([...filters, { field: fields[0], kind: 'categorical', values: [] }]);
  };

  const remove = (i: number) => onChange(filters.filter((_, idx) => idx !== i));

  const changeKind = (i: number, kind: FilterSpec['kind']) => {
    const field = filters[i].field;
    update(i, kind === 'categorical' ? { field, kind, values: [] } : { field, kind });
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <Label>Filtros</Label>
        <Button variant="link" size="sm" onClick={add} disabled={fields.length === 0}>
          <Plus className="h-4 w-4" /> Agregar filtro
        </Button>
      </div>
      {fields.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Selecciona una fuente de datos con columnas para agregar filtros.
        </p>
      )}

      <div className="mt-2 space-y-2">
        {filters.map((f, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-40">
              <FieldSelect
                value={f.field}
                onChange={(v) => v && update(i, { ...f, field: v })}
                options={fields}
                placeholder="Columna"
              />
            </div>

            <div className="w-32">
              <Select value={f.kind} onValueChange={(v) => changeKind(i, v as FilterSpec['kind'])}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="categorical">Categórico</SelectItem>
                  <SelectItem value="numeric">Numérico</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {f.kind === 'categorical' ? (
              <CategoricalValuePicker
                index={i}
                selected={f.values ?? []}
                available={getValues(f.field)}
                onChange={(values) => update(i, { ...f, values })}
              />
            ) : (
              <>
                <Input
                  className="w-24"
                  type="number"
                  placeholder="min"
                  value={f.min ?? ''}
                  onChange={(e) => update(i, { ...f, min: parseBound(e.target.value) })}
                />
                <Input
                  className="w-24"
                  type="number"
                  placeholder="max"
                  value={f.max ?? ''}
                  onChange={(e) => update(i, { ...f, max: parseBound(e.target.value) })}
                />
              </>
            )}

            <Button
              variant="ghost"
              size="icon"
              className="text-destructive"
              aria-label="Eliminar filtro"
              onClick={() => remove(i)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default FiltersEditor;
